
import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, MantineColorScheme } from '@mantine/core';
import { ThemeProvider, useTheme } from '@/components/theme/ThemeProvider';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { EnhancedAuthProvider } from '@/context/auth/EnhancedAuthContext';
import { setMockNow } from '@/utils/clock';

import { EmailProvider } from '@/context/EmailContext';
import { TwilioProvider } from '@/context/TwilioContext';
import { NotificationProvider } from '@/context/NotificationContext';
import { SessionExpirationWarning } from '@/components/auth/SessionExpirationWarning';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

// Mantine theme wrapper that syncs with Tailwind dark mode
const MantineThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme();
  
  // Convert theme to Mantine color scheme
  const getMantineColorScheme = (): 'dark' | 'light' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme === 'dark' ? 'dark' : 'light';
  };

  const [colorScheme, setColorScheme] = React.useState<'dark' | 'light'>(getMantineColorScheme);

  useEffect(() => {
    const newColorScheme = getMantineColorScheme();
    setColorScheme(newColorScheme);
  }, [theme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => setColorScheme(getMantineColorScheme());
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  return (
    <MantineProvider
      theme={{
        primaryColor: 'blue',
        fontFamily: 'inherit',
      }}
      forceColorScheme={colorScheme}
    >
      {children}
    </MantineProvider>
  );
};

// Inner component that has access to auth context
const AuthenticatedProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return (
      <EnhancedAuthProvider>
        <NotificationProvider>
          <EmailProvider>
            <TwilioProvider>
              <SessionExpirationWarning />
              {children}
            </TwilioProvider>
          </EmailProvider>
        </NotificationProvider>
      </EnhancedAuthProvider>
    );
  }
  
  return <>{children}</>;
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  // Handle mock clock URL parameter on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mockNow = urlParams.get('mockNow');
    if (mockNow) {
      setMockNow(mockNow);
      console.info('🕐 Mock time set from URL parameter:', mockNow);
    }
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <MantineThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AuthenticatedProviders>
                {children}
              </AuthenticatedProviders>
            </AuthProvider>
          </BrowserRouter>
        </MantineThemeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
